type DecodedImage = {
  uri: string;
  expiresAtMs: number | null;
};

const decodedImages = new Map<string, DecodedImage>();

/**
 * Browse-art URLs contain a freshly signed token between `/p/` and the
 * stable R2 object key. CatalogProvider can be reconstructed by Expo's web
 * history restore, which produces a new token for the same image. Readiness
 * therefore has to follow the object, not the temporary bearer URL.
 */
function imageKey(uri: string): string {
  const match = /^(.*\/p\/)[^/]+\/(.+)$/.exec(uri);
  return match ? `${match[1]}<token>/${match[2]}` : uri;
}

function tokenExpiryMs(uri: string): number | null {
  const match = /\/p\/([^/.]+)(?:\.[^/]+)?\//.exec(uri);
  if (!match || typeof atob !== "function") return null;
  try {
    const base64 = match[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const claims = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof claims.exp === "number" ? claims.exp * 1000 : null;
  } catch {
    return null;
  }
}

function isUsable(entry: DecodedImage): boolean {
  return entry.expiresAtMs == null || entry.expiresAtMs > Date.now() + 60_000;
}

/**
 * React components remount during navigation even when the image cache still
 * has their pixels. Remember successful preparation/paints so those remounts
 * do not replay a placeholder-to-artwork transition.
 */
export function hasDecodedImage(uri: string): boolean {
  const entry = decodedImages.get(imageKey(uri));
  return entry != null && isUsable(entry);
}

/** Return the already-painted URL for this logical asset, even if `uri` has a new token. */
export function getDecodedImageUri(uri: string): string | null {
  const entry = decodedImages.get(imageKey(uri));
  return entry && isUsable(entry) ? entry.uri : null;
}

export function markImageDecoded(uri: string): void {
  decodedImages.set(imageKey(uri), { uri, expiresAtMs: tokenExpiryMs(uri) });
}

/** Bearer URLs must never survive an authenticated-account change. */
export function clearDecodedImages(): void {
  decodedImages.clear();
}
